// src/components/Login/AdminLogin.tsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import "./Login.css";
import logo from "../../assets/dr-is-in.png";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");
const raw = axios.create({ baseURL: API_BASE, timeout: 15000 });

const STORES: Storage[] = [sessionStorage, localStorage];

function removeKeys(keys: string[]) {
  for (const store of STORES) {
    for (const k of keys) {
      try {
        store.removeItem(k);
      } catch {}
    }
  }
}

function setBoth(key: string, value: string) {
  for (const store of STORES) {
    try {
      store.setItem(key, value);
    } catch {}
  }
}

function clearOtherRoles() {
  removeKeys([
    "doctor_token",
    "doctor_access_token",
    "doctor_jwt",
    "student_token",
    "student_access_token",
    "student_jwt",
    "parent_token",
    "parent_access_token",
    "teacher_token",
    "teacher_access_token",
    "pt_token",
    "pt_access_token",
  ]);
}

function clearAdminTokens() {
  removeKeys(["admin_token", "admin_access_token", "admin_jwt"]);
}

function getApiErrorMessage(err: any): string {
  const data = err?.response?.data;

  if (!data) {
    return `Cannot reach backend at ${API_BASE}`;
  }

  if (Array.isArray(data?.detail)) {
    return data.detail
      .map((item: any) => {
        const where = Array.isArray(item?.loc) ? item.loc.join(" > ") : "";
        const msg = typeof item?.msg === "string" ? item.msg : "Validation error";
        return where ? `${where}: ${msg}` : msg;
      })
      .join(" | ");
  }

  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.message === "string") return data.message;
  if (typeof data === "string") return data;

  try {
    return JSON.stringify(data);
  } catch {
    return "Login failed";
  }
}

export default function AdminLogin() {
  const navigate = useNavigate();

  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  function storeAdminSession(token: string, adminId?: number) {
    const t = (token || "").trim();
    if (!t) return;

    clearOtherRoles();
    clearAdminTokens();

    setBoth("role", "admin");
    setBoth("user_type", "admin");

    setBoth("admin_access_token", t);
    setBoth("admin_token", t);
    setBoth("admin_jwt", t);

    setBoth("access_token", t);
    setBoth("token", t);

    if (adminId != null) setBoth("admin_id", String(adminId));
    else removeKeys(["admin_id"]);
  }

  async function verifyAdmin(token: string) {
    await raw.get("/admin/doctors/roster", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const form = new URLSearchParams();
      form.append("username", username.trim());
      form.append("password", password);

      const res = await raw.post("/auth/login_admin", form, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const data = res?.data || {};
      const token = String(data?.access_token || data?.token || "").trim();
      const adminId = data?.admin_id;

      if (!token) {
        setError("Login succeeded, but no token was returned from the server.");
        return;
      }

      storeAdminSession(token, adminId);

      await verifyAdmin(token);

      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error("Admin login error:", err?.response?.data || err);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="login-container">
      <div className="login-box">
        <img src={logo} alt="Doctor Is In Logo" className="login-logo" />

        <h2 className="login-title">Admin Login</h2>
        <div className="login-tagline">AI Wellness Advisor with a human touch.</div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="admin_username">Username</label>
            <input
              id="admin_username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="admin_password">Password</label>
            <input
              id="admin_password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          {error ? (
            <div
              style={{
                marginTop: 10,
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(180, 30, 30, 0.10)",
                border: "1px solid rgba(180, 30, 30, 0.25)",
                color: "#8a1f1f",
                fontSize: 14,
                lineHeight: 1.4,
                whiteSpace: "pre-wrap",
              }}
            >
              {error}
            </div>
          ) : null}

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? "Logging in..." : "Admin Login"}
          </button>
        </form>

        <div style={{ marginTop: 10, fontSize: 13 }}>
          <Link to="/login" style={{ textDecoration: "none" }}>
            Back to Login
          </Link>
        </div>

        <div style={{ marginTop: 14, fontSize: 12, opacity: 0.8 }}>
          © {year} Doctor Is In
        </div>
      </div>
    </div>
  );
}